import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { AppUser, Restaurant } from '@/types/app'

export function useAuthInit() {
  const { setUser, setRestaurant, setLoading, clear } = useAuthStore()
  const inFlight = useRef(false)

  useEffect(() => {
    let active = true

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!active) return

        if (!session || event === 'SIGNED_OUT') {
          clear()
          return
        }

        if (
          event === 'INITIAL_SESSION' ||
          event === 'SIGNED_IN' ||
          event === 'TOKEN_REFRESHED'
        ) {
          if (inFlight.current) return
          inFlight.current = true
          setLoading(true)

          try {
            // Use the session token directly — the Supabase JS client can be in a
            // stale-refresh state immediately after SIGNED_IN, causing .from() queries
            // to hang while the client retries. Raw fetch with the callback token is
            // always authoritative and avoids that deadlock.
            const sbUrl = import.meta.env.VITE_SUPABASE_URL as string
            const sbKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string
            const headers = {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': sbKey,
              'Accept': 'application/json',
            }

            const userResp = await fetch(
              `${sbUrl}/rest/v1/users?id=eq.${session.user.id}&select=*&limit=1`,
              { headers }
            )
            if (!active) return
            if (!userResp.ok) {
              await supabase.auth.signOut({ scope: 'local' })
              if (active) clear()
              return
            }
            const usersData = await userResp.json() as AppUser[]
            const user = usersData[0] ?? null
            if (!user) {
              await supabase.auth.signOut({ scope: 'local' })
              if (active) clear()
              return
            }

            const restResp = await fetch(
              `${sbUrl}/rest/v1/restaurants?id=eq.${user.restaurant_id}&select=*&limit=1`,
              { headers }
            )
            if (!active) return
            const restsData = restResp.ok ? await restResp.json() as Restaurant[] : []
            const restaurant = restsData[0] ?? null

            if (restaurant) setRestaurant(restaurant)
            setUser(user)
          } catch {
            if (active) clear()
          } finally {
            if (active) setLoading(false)
            inFlight.current = false
          }
        }
      }
    )

    return () => {
      active = false
      inFlight.current = false
      subscription.unsubscribe()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}

export function useSignOut() {
  const navigate = useNavigate()
  const clear = useAuthStore(s => s.clear)

  return async () => {
    await supabase.auth.signOut()
    clear()
    navigate('/login')
  }
}
